import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { getFirebaseInstances } from '../../config/firebase/firebase';
import { AuthContext } from './AuthProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { BRAND_MESSAGING } from '../../constants/brandMessaging';
import {
  validateLoginEmail,
  validateLoginPassword,
  mapLoginErrorCodeToMessage,
  mapPasswordResetErrorCodeToMessage
} from './loginAuthHelpers';
import {
  getLoginEmailActionNotice,
  isEmailActionLoginHold,
} from './auth/authActionHelpers';
import styles from './LoginEnhanced.module.css';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOutUser, canAccessTerminal } = useContext(AuthContext);
  const emailFromLink = searchParams.get('email') || '';
  const holdLoginForm = isEmailActionLoginHold(searchParams);
  const emailActionNotice = getLoginEmailActionNotice(searchParams);
  const postActionSignOutRef = useRef(false);
  const [formData, setFormData] = useState({ email: emailFromLink, password: '' });
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [sessionActionLoading, setSessionActionLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [error, setError] = useState({ email: '', password: '', general: '' });
  const [showPassword, setShowPassword] = useState(false);

  // Real-time validation
  const validateEmail = validateLoginEmail;
  const validatePassword = validateLoginPassword;

  useEffect(() => {
    if (holdLoginForm) {
      if (user && !postActionSignOutRef.current) {
        postActionSignOutRef.current = true;
        signOutUser('post-email-action');
      }
      return;
    }
    if (!user) return;
    navigate(canAccessTerminal ? '/terminal' : '/signup-recovery', { replace: true });
  }, [canAccessTerminal, holdLoginForm, navigate, signOutUser, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setResetMessage('');
    setError((prev) => ({ ...prev, [name]: '', general: '' }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let err = '';
    if (name === 'email') err = validateEmail(value);
    if (name === 'password') err = validatePassword(value);
    setError((prev) => ({ ...prev, [name]: err }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(formData.email);
    const passErr = validatePassword(formData.password);
    if (emailErr || passErr) {
      setError({ email: emailErr, password: passErr, general: '' });
      return;
    }
    setLoading(true);
    setError({ email: '', password: '', general: '' });
    try {
      const { auth } = await getFirebaseInstances();
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      navigate('/terminal', { replace: true });
    } catch (err) {
      const code = err?.code || 'unknown';
      console.warn('Email/password sign-in failed', {
        code,
        email: formData.email?.trim() || ''
      });
      const msg = mapLoginErrorCodeToMessage(err?.code);
      setError((prev) => ({ ...prev, general: msg }));
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setResetMessage('');
    setError((prev) => ({ ...prev, general: '' }));

    const email = formData.email.trim();
    const emailErr = validateEmail(email);
    if (emailErr) {
      setError((prev) => ({ ...prev, email: emailErr }));
      return;
    }

    setResetLoading(true);
    try {
      const { auth } = await getFirebaseInstances();
      await sendPasswordResetEmail(auth, email);
      setResetMessage('Password reset email sent. Check your inbox and spam folder.');
    } catch (err) {
      const code = err?.code || 'unknown';
      console.warn('Password reset failed', { code, email });
      const msg = mapPasswordResetErrorCodeToMessage(err?.code);
      setError((prev) => ({ ...prev, general: msg }));
    } finally {
      setResetLoading(false);
    }
  };

  const handleExistingSessionSignOut = async () => {
    setSessionActionLoading(true);
    try {
      await signOutUser('login-session');
      setError({ email: '', password: '', general: '' });
    } catch (error) {
      setError((prev) => ({ ...prev, general: 'Failed to sign out. Please try again.' }));
    } finally {
      setSessionActionLoading(false);
    }
  };

  const handleContinueToTerminal = () => {
    navigate('/terminal', { replace: true });
  };

  return (
    <div className={styles.splitLayout}>
      {/* Left Panel: Branding & Trust */}
      <div className={styles.leftPanel}>
        <img
          src={`${process.env.PUBLIC_URL}/images/brand/verdekai-linkedin-company-logo.png`}
          alt="VerdeKai Legal Terminal"
          className={styles.brandImage}
        />
        <div className={styles.brandContent}>
          <h1 className={styles.platformName}>VerdeKai Legal Terminal</h1>
          <h2 className={styles.welcome}>{BRAND_MESSAGING.login.heading}</h2>
          <p className={styles.trustMsg}>{BRAND_MESSAGING.login.subheading}</p>
          <div className={styles.statsRow}>
            {BRAND_MESSAGING.login.stats.map((stat) => (
              <div key={stat.label}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
          <ul className={styles.trustList}>
            {BRAND_MESSAGING.login.trustBullets.map((bullet) => (
              <li key={bullet}><span className={styles.trustBadge}></span> {bullet}</li>
            ))}
          </ul>
        </div>
      </div>
      {/* Right Panel: Sign In Form */}
      <div className={styles.rightPanel}>
        {user && !holdLoginForm ? (
          <div className={styles.formCard}>
            <h2 className={styles.formTitle}>Session Detected</h2>
            <p className={styles.formSubtitle}>
              You are already authenticated in this tab. Choose how to proceed.
            </p>
            <p className={styles.formSubtitle} style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              Signed in as <strong>{user.email}</strong>
            </p>
            {error.general && <div className={styles.errorMsg}>{error.general}</div>}
            <div className={styles.sessionActions}>
              <button
                type="button"
                className={styles.signInBtn}
                onClick={handleContinueToTerminal}
                disabled={sessionActionLoading}
              >
                Continue to Terminal
              </button>
              <button
                type="button"
                className={styles.secondaryActionBtn}
                onClick={handleExistingSessionSignOut}
                disabled={sessionActionLoading}
              >
                {sessionActionLoading ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          </div>
        ) : (
        <form className={styles.formCard} onSubmit={handleSubmit} autoComplete="on" noValidate>
          <h2 className={styles.formTitle}>Sign In</h2>
          <p className={styles.formSubtitle}>Access your legal terminal workspace</p>
          {error.general && <div className={styles.errorMsg}>{error.general}</div>}
          {emailActionNotice && <div className={styles.successMsg}>{emailActionNotice}</div>}
          {resetMessage && <div className={styles.successMsg}>{resetMessage}</div>}
          <div className={styles.formGroup}>
            <div className={styles.floatingLabelGroup}>
              <input
                type="email"
                name="email"
                id="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={error.email ? styles.inputError : ''}
                required
                disabled={loading}
              />
              <label htmlFor="email" className={formData.email ? styles.hasValue : ''}>Email Address</label>
            </div>
            {error.email && <span className={styles.errorText}>{error.email}</span>}
          </div>
          <div className={styles.formGroup}>
            <div className={styles.floatingLabelGroup}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={error.password ? styles.inputError : ''}
                required
                disabled={loading}
              />
              <label htmlFor="password" className={formData.password ? styles.hasValue : ''}>Password</label>
              <button type="button" className={styles.passwordToggle} onClick={() => setShowPassword(v => !v)} tabIndex={-1} aria-label="Toggle password visibility">
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            {error.password && <span className={styles.errorText}>{error.password}</span>}
          </div>
          <div className={styles.formOptions}>
            <label className={styles.rememberMe}><input type="checkbox" disabled={loading} /> Remember me</label>
            <button
              type="button"
              className={styles.forgotLinkBtn}
              onClick={handleForgotPassword}
              disabled={loading || resetLoading}
            >
              {resetLoading ? 'Sending reset...' : 'Forgot password?'}
            </button>
          </div>
          <button type="submit" className={styles.signInBtn} disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</button>
          <div className={styles.signupPrompt}>
            Don&apos;t have an account? <a href="/signup-access">Sign up</a>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default Login;
